import { ICMS, ICMSIMAGE } from "@/types/home.types";

export const decodeHtml = (html: string) => {
  if (typeof window === "undefined")
    return html.replace(/<p>([\s\S]*?)<\/p>$/g, "$1");
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  const decodedHtml = txt.value;
  return decodedHtml.replace(/^<p>([\s\S]*?)<\/p>$/g, "$1");
};

export const getTicketPrice = (date: string): number => {
  if (!date) return 0;
  const selectedDate = new Date(date);
  const day = selectedDate.getDay();
  return day === 0 || day === 6 ? 1000 : 900;
};

export const getBreakdown = (date: string, passengers: number) => {
  const inclusivePrice = getTicketPrice(date);
  const totalInclusive = inclusivePrice * passengers;
  const basePerTicket = inclusivePrice / 1.18;
  const baseTotal = basePerTicket * passengers;
  const cgst = baseTotal * 0.09;
  const sgst = baseTotal * 0.09;
  const perTicket = inclusivePrice;
  const grandTotal = totalInclusive;
  return { perTicket, cgst, sgst, grandTotal };
};

export function formatIndianNumber(number: number, decimal: any = 2): string {
  if (number === null || number === undefined) return "";
  let [integerPart, decimalPart] = number.toFixed(decimal).split(".");
  let lastThreeDigits = integerPart.slice(-3);
  let otherDigits = integerPart.slice(0, -3);
  if (otherDigits !== "") lastThreeDigits = "," + lastThreeDigits;
  let formattedOtherDigits = otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",");
  return formattedOtherDigits + lastThreeDigits + "." + decimalPart;
}

export function createLookupObject<
  T extends Record<string, any>,
  K extends keyof T,
>(array: T[], key: K): Record<T[K] & (string | number | symbol), T> {
  const lookup: Record<string | number | symbol, T> = {};
  for (const item of array) lookup[item[key] as any] = item;
  return lookup as Record<T[K] & (string | number | symbol), T>;
}

export const processCMSContent = (cms: ICMS): string => {
  if (!cms?.Template) return "";
  let processedContent = decodeHtml(cms.Template);
  if (cms.cms_image_details && cms.cms_image_details.length > 0) {
    cms.cms_image_details.forEach((img: ICMSIMAGE) => {
      const imgNameParts = img.Name?.split("-");
      const imgIdentifier = imgNameParts?.length
        ? imgNameParts[imgNameParts.length - 1]
        : img.Name;
      if (imgIdentifier === "bg") {
        processedContent = processedContent.replace(
          `id="${img.Name}">`,
          `id="${img.Name}" style="background-image:url('${img.Image}')">`,
        );
      } else {
        const placeholderPattern = new RegExp(
          `id="${img.Name}">\\s*</div>`,
          "g",
        );
        processedContent = processedContent.replace(
          placeholderPattern,
          `id="${img.Name}"><img src="${img.Image}" alt="${img.AltName || ""}" class="img-fluid" /></div>`,
        );
      }
    });
  }
  return processedContent;
};

export function generateValueLabelRange<T extends number | string>(
  start: number,
  end: number,
  as: "number" | "string",
): { value: T; label: T }[] {
  const result: { value: T; label: T }[] = [];
  for (let i = start; i <= end; i++) {
    const val = (as === "string" ? String(i) : i) as T;
    result.push({ value: val, label: val });
  }
  return result;
}

export const parseQueryParams = (search: string): Record<string, string> => {
  const queryParams = new URLSearchParams(search);
  const params: Record<string, string> = {};
  queryParams.forEach((value, key) => {
    params[key] = value;
  });
  return params;
};

export const calculateReadingTime = (
  text: string,
  wordsPerMinute = 150,
): string => {
  if (!text) return "1 min read";

  // Remove HTML tags if present
  const cleanText = text.replace(/<[^>]*>/g, "");

  // Count words (split by spaces and filter out empty strings)
  const wordCount = cleanText
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length;

  // Calculate reading time in minutes
  const minutes = Math.ceil(wordCount / wordsPerMinute);

  // Return formatted string
  return `${Math.max(1, minutes)} min read`;
};
